import type { ApiResponseType, PaginatedResponseType } from '~/types/api'
import api, { apiData } from './api'

export type CompanyRoleType = 'owner' | 'admin' | 'member'

export interface UserType {
  id: number
  email: string
  name: string
  document?: string | null
  roleId?: number | null
  isRoot?: boolean | null
  status?: number | null
  /** Rol dentro de la empresa activa (owner/admin/member), no el rol de permisos. */
  companyRole?: CompanyRoleType
  /** Nombre del rol de permisos, resuelto por el backend para no volver a pedirlo. */
  roleName?: string | null
}

export interface NamedPermissionType {
  id: number
  name: string
  description?: string | null
}

/** Membresía del usuario en una empresa. */
export interface UserCompanyType {
  companyId: string
  name: string
  slug?: string | null
  role: CompanyRoleType
  isDefault: boolean
  /** Falso cuando quien consulta no administra esa empresa: se muestra, no se edita. */
  manageable: boolean
}

/** Ficha completa que consume `admin/usuarios/[id]`. */
export interface UserDetailType extends UserType {
  googleLinked: boolean
  githubLinked: boolean
  createdAt?: string | null
  updatedAt?: string | null
  /** Permisos concedidos directamente a este usuario. */
  permissions: NamedPermissionType[]
  /** Permisos que ya trae su rol: se ven marcados pero se editan desde el rol. */
  rolePermissions: NamedPermissionType[]
  companies: UserCompanyType[]
}

export interface CreateUserPayloadType {
  email: string
  password: string
  name: string
  document?: string
  roleId?: number
  companyRole?: CompanyRoleType
}

export interface UpdateUserPayloadType {
  email?: string
  name?: string
  document?: string
  password?: string
  roleId?: number | null
  status?: number
  companyRole?: CompanyRoleType
  /** Sólo la acepta el backend si quien edita es super administrador. */
  isRoot?: boolean
}

export interface SyncCompanyPayloadType {
  companyId: string
  role: CompanyRoleType
  isDefault?: boolean
}

export const userService = {
  async list(params?: { page?: number; limit?: number; name?: string; email?: string; document?: string; status?: string }) {
    return apiData(api.get<ApiResponseType<PaginatedResponseType<UserType>>>('/users', { params }))
  },

  async get(id: number) {
    return apiData(api.get<ApiResponseType<UserDetailType>>(`/users/${id}`))
  },

  async create(payload: CreateUserPayloadType) {
    return apiData(api.post<ApiResponseType<UserType>>('/users', payload))
  },

  async update(id: number, payload: UpdateUserPayloadType) {
    return apiData(api.put<ApiResponseType<UserType>>(`/users/${id}`, payload))
  },

  async delete(id: number) {
    return apiData(api.delete<ApiResponseType<boolean>>(`/users/${id}`))
  },

  /** Quita varios usuarios de la empresa activa de una vez. */
  async bulkDelete(ids: number[]) {
    return apiData(api.post<ApiResponseType<{ deleted: number }>>('/users/bulk-delete', { ids }))
  },

  /** Reemplaza de una vez los permisos directos: el backend calcula altas y bajas. */
  async syncPermissions(id: number, permissionIds: number[]) {
    return apiData(api.put<ApiResponseType<boolean>>(`/users/${id}/permissions`, { permissionIds }))
  },

  async listCompanies(id: number) {
    return apiData(api.get<ApiResponseType<UserCompanyType[]>>(`/users/${id}/companies`))
  },

  /** Sincroniza las empresas del usuario; las que no administras se conservan. */
  async syncCompanies(id: number, companies: SyncCompanyPayloadType[]) {
    return apiData(api.put<ApiResponseType<UserCompanyType[]>>(`/users/${id}/companies`, { companies }))
  }
}
